import { useEffect, useMemo, useState } from "react";
import { PlanLocked } from "../../components/PlanLocked";
import { useEntitlements } from "../../hooks/useEntitlements";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { paths, type Estimate, type Invoice, type Job } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { downloadCsv, csvDate, csvMoney } from "../../lib/csv";
import { useAuth } from "../../hooks/useAuth";
import { CommissionsCard } from "../../components/CommissionsCard";
import { LeadSourcesCard } from "../../components/LeadSourcesCard";
import { SalesByCard } from "../../components/SalesByCard";
import { ReferralsCard } from "../../components/ReferralsCard";
import { useDepartments } from "../../hooks/useDepartments";

// Reports: the numbers an owner actually runs the week on — collected vs
// billed, AR aging, estimate win rate, tech throughput — plus "Ask Clevis AI"
// over the same digest. Loaded once per visit (not live) and aggregated
// client-side; Firestore→BigQuery stays the path for heavier analytics.

type InvRow = Invoice & { id: string };
type EstRow = Estimate & { id: string };
type JobRow = Job & { id: string };

const money = (c: number) =>
  (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const RANGES = [
  { key: "month", label: "This month" },
  { key: "last", label: "Last month" },
  { key: "90", label: "Last 90 days" },
  { key: "ytd", label: "This year" },
] as const;
type RangeKey = (typeof RANGES)[number]["key"];

function rangeBounds(key: RangeKey): { start: number; end: number } {
  const now = new Date();
  if (key === "month") return { start: new Date(now.getFullYear(), now.getMonth(), 1).getTime(), end: Date.now() };
  if (key === "last")
    return {
      start: new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime(),
      end: new Date(now.getFullYear(), now.getMonth(), 1).getTime(),
    };
  if (key === "90") return { start: Date.now() - 90 * 86400000, end: Date.now() };
  return { start: new Date(now.getFullYear(), 0, 1).getTime(), end: Date.now() };
}

export function ReportsPage() {
  const { tenantId, role, user } = useAuth();
  const { hasFeature } = useEntitlements();
  const dept = useDepartments();
  const [invoices, setInvoices] = useState<InvRow[]>([]);
  const [estimates, setEstimates] = useState<EstRow[]>([]);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [members, setMembers] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);
  const [range, setRange] = useState<RangeKey>("month");

  useEffect(() => {
    if (!tenantId) return;
    void (async () => {
      const [inv, est, jb, mem] = await Promise.all([
        getDocs(query(collection(db, paths.tenantInvoices(tenantId)), orderBy("createdAt", "desc"))),
        getDocs(query(collection(db, paths.tenantEstimates(tenantId)), orderBy("createdAt", "desc"))),
        getDocs(query(collection(db, paths.tenantJobs(tenantId)), orderBy("createdAt", "desc"))),
        getDocs(collection(db, paths.tenantMembers(tenantId))),
      ]);
      setInvoices(inv.docs.map((d) => ({ id: d.id, ...(d.data() as Invoice) })));
      setEstimates(est.docs.map((d) => ({ id: d.id, ...(d.data() as Estimate) })));
      setJobs(jb.docs.map((d) => ({ id: d.id, ...(d.data() as Job) })));
      const m: Record<string, string> = {};
      mem.docs.forEach((d) => (m[d.id] = (d.get("displayName") as string) || (d.get("email") as string) || "?"));
      setMembers(m);
      setLoaded(true);
    })();
  }, [tenantId]);

  const { start, end } = rangeBounds(range);

  const digest = useMemo(() => {
    const live = invoices.filter((i) => i.status !== "void" && i.status !== "draft");
    // Collected: actual payment records that landed in the range.
    let collectedCents = 0;
    for (const i of live)
      for (const p of i.payments) if (p.at >= start && p.at < end) collectedCents += p.amountCents;
    const billedRange = live.filter((i) => i.createdAt >= start && i.createdAt < end);
    const billedCents = billedRange.reduce((s, i) => s + i.totalCents, 0);

    // AR is a now-snapshot, never range-filtered.
    const open = live.filter((i) => i.totalCents > i.amountPaidCents);
    const aging = { current: 0, d30: 0, d60: 0, d90: 0 };
    const agingRows: Array<{ number: number; customer: string; balance: number; dueAt: number | null; bucket: string }> = [];
    for (const i of open) {
      const balance = i.totalCents - i.amountPaidCents;
      const due = i.dueAt ?? (i.sentAt ? i.sentAt + 30 * 86400000 : i.createdAt + 30 * 86400000);
      const daysOver = Math.floor((Date.now() - due) / 86400000);
      const bucket = daysOver <= 0 ? "current" : daysOver <= 30 ? "d30" : daysOver <= 60 ? "d60" : "d90";
      aging[bucket] += balance;
      agingRows.push({
        number: i.number,
        customer: i.customerName,
        balance,
        dueAt: i.dueAt,
        bucket: bucket === "current" ? "Not due" : bucket === "d30" ? "1–30 late" : bucket === "d60" ? "31–60 late" : "60+ late",
      });
    }
    agingRows.sort((a, b) => b.balance - a.balance);

    const estRange = estimates.filter((e) => e.createdAt >= start && e.createdAt < end);
    const won = estRange.filter((e) => e.status === "accepted").length;
    const lost = estRange.filter((e) => e.status === "declined").length;
    const pipelineCents = estimates
      .filter((e) => e.status === "sent")
      .reduce((s, e) => s + e.totalCents, 0);

    const doneRange = jobs.filter((j) => j.status === "done" && (j.doneAt ?? j.updatedAt) >= start && (j.doneAt ?? j.updatedAt) < end);
    const techCounts = new Map<string, number>();
    for (const j of doneRange) {
      const uid = j.assignedUids[0];
      if (uid) techCounts.set(uid, (techCounts.get(uid) ?? 0) + 1);
    }
    const techBoard = [...techCounts.entries()]
      .map(([uid, n]) => ({ name: members[uid] ?? "?", jobs: n }))
      .sort((a, b) => b.jobs - a.jobs);

    const custTotals = new Map<string, number>();
    for (const i of billedRange) custTotals.set(i.customerName || "No name", (custTotals.get(i.customerName || "No name") ?? 0) + i.totalCents);
    const topCustomers = [...custTotals.entries()]
      .map(([name, cents]) => ({ name, cents }))
      .sort((a, b) => b.cents - a.cents)
      .slice(0, 5);

    // Collected by month, trailing six, for the bars.
    const monthly: Array<{ label: string; cents: number }> = [];
    for (let k = 5; k >= 0; k--) {
      const d = new Date();
      const mStart = new Date(d.getFullYear(), d.getMonth() - k, 1).getTime();
      const mEnd = new Date(d.getFullYear(), d.getMonth() - k + 1, 1).getTime();
      let cents = 0;
      for (const i of live) for (const p of i.payments) if (p.at >= mStart && p.at < mEnd) cents += p.amountCents;
      monthly.push({ label: new Date(mStart).toLocaleDateString(undefined, { month: "short" }), cents });
    }

    return {
      collectedCents,
      billedCents,
      invoiceCount: billedRange.length,
      outstandingCents: open.reduce((s, i) => s + (i.totalCents - i.amountPaidCents), 0),
      aging,
      agingRows,
      estimatesSent: estRange.filter((e) => e.status !== "draft").length,
      won,
      lost,
      winRate: won + lost > 0 ? Math.round((100 * won) / (won + lost)) : null,
      pipelineCents,
      jobsDone: doneRange.length,
      jobsUpcoming: jobs.filter((j) => j.status === "scheduled" && (j.startAt ?? 0) > Date.now()).length,
      techBoard,
      topCustomers,
      monthly,
    };
  }, [invoices, estimates, jobs, members, start, end]);

  const maxMonthly = Math.max(1, ...digest.monthly.map((m) => m.cents));

  function exportAging() {
    downloadCsv(
      "ar-aging",
      ["Invoice", "Customer", "Balance", "Due", "Bucket"],
      digest.agingRows.map((r) => [r.number, r.customer, csvMoney(r.balance), csvDate(r.dueAt), r.bucket]),
    );
  }

  if (!loaded) return <p className="text-sm text-steel">Crunching your numbers…</p>;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-bold">Reports</h1>
        <div className="flex rounded-lg border border-steel/30 p-0.5 text-xs">
          {RANGES.map((r) => (
            <button
              key={r.key}
              className={cn("rounded-md px-3 py-1", range === r.key ? "bg-ink text-white" : "text-steel")}
              onClick={() => setRange(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ["Collected", money(digest.collectedCents)],
          ["Billed", money(digest.billedCents)],
          ["Outstanding (all)", money(digest.outstandingCents)],
          ["Jobs done", String(digest.jobsDone)],
        ].map(([label, value]) => (
          <Card key={label} className="py-3 text-center">
            <div className="text-lg font-bold text-ink">{value}</div>
            <div className="text-[11px] text-steel">{label}</div>
          </Card>
        ))}
      </div>

      <Card>
        <CardTitle>Collected by month</CardTitle>
        <div className="flex h-28 items-end gap-2">
          {digest.monthly.map((m) => (
            <div key={m.label} className="flex flex-1 flex-col items-center gap-1">
              <div className="text-[10px] text-steel">{m.cents > 0 ? money(m.cents) : ""}</div>
              <div
                className="w-full rounded-t bg-brandorange/80"
                style={{ height: `${Math.max(2, (88 * m.cents) / maxMonthly)}px` }}
              />
              <div className="text-[10px] text-steel">{m.label}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid gap-3 md:grid-cols-2">
        <Card>
          <CardTitle>Estimates</CardTitle>
          <p className="text-sm text-ink">
            {digest.estimatesSent} sent · {digest.won} won · {digest.lost} lost
            {digest.winRate !== null ? ` · ${digest.winRate}% win rate` : ""}
          </p>
          <p className="mt-1 text-xs text-steel">Open pipeline (sent, undecided): {money(digest.pipelineCents)}</p>
          <p className="mt-1 text-xs text-steel">Upcoming scheduled jobs: {digest.jobsUpcoming}</p>
        </Card>
        <Card>
          <CardTitle>Tech throughput</CardTitle>
          {digest.techBoard.length === 0 ? (
            <p className="text-xs text-steel">No completed jobs in this range.</p>
          ) : (
            digest.techBoard.map((t) => (
              <div key={t.name} className="flex items-center justify-between text-sm">
                <span className="text-ink">{t.name}</span>
                <span className="text-steel">{t.jobs} jobs</span>
              </div>
            ))
          )}
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between">
          <CardTitle className="mb-0">Who owes you — AR aging</CardTitle>
          <button className="text-xs text-steel hover:text-ink" onClick={exportAging}>
            Export CSV
          </button>
        </div>
        <div className="mt-2 grid grid-cols-4 gap-2 text-center text-xs">
          {[
            ["Not due", digest.aging.current],
            ["1–30 late", digest.aging.d30],
            ["31–60 late", digest.aging.d60],
            ["60+ late", digest.aging.d90],
          ].map(([label, cents], i) => (
            <div key={label as string} className={cn("rounded-lg px-2 py-2", i >= 2 ? "bg-danger/10" : "bg-cream")}>
              <div className={cn("font-bold", i >= 2 ? "text-danger" : "text-ink")}>{money(cents as number)}</div>
              <div className="text-steel">{label}</div>
            </div>
          ))}
        </div>
        <div className="mt-2 max-h-56 space-y-1 overflow-y-auto">
          {digest.agingRows.slice(0, 50).map((r) => (
            <div key={r.number} className="flex items-center justify-between text-xs">
              <span className="truncate text-ink">
                #{r.number} · {r.customer || "No name"}
              </span>
              <span className="shrink-0 text-steel">
                {money(r.balance)} · {r.bucket}
              </span>
            </div>
          ))}
          {digest.agingRows.length === 0 ? <p className="text-xs text-steel">Nothing outstanding. 🎉</p> : null}
        </div>
      </Card>

      <SalesByCard
        invoices={invoices}
        jobs={jobs}
        members={members}
        departments={dept.departments}
        start={start}
        end={end}
        rangeLabel={RANGES.find((r) => r.key === range)!.label}
        // Office staff let into Reports (a sales rep) see only their own sales.
        ownOnlyUid={role === "staff" || role === "tech" ? (user?.uid ?? null) : null}
      />

      {tenantId ? <LeadSourcesCard tenantId={tenantId} invoices={invoices} start={start} end={end} rangeLabel={RANGES.find((r) => r.key === range)!.label} /> : null}

      {tenantId && hasFeature("referrals") ? <ReferralsCard tenantId={tenantId} start={start} end={end} rangeLabel={RANGES.find((r) => r.key === range)!.label} /> : null}

      {/* Commission rates are pay data: admins and the owner only. */}
      {tenantId && (role === "admin" || role === "owner") ? (
        <CommissionsCard tenantId={tenantId} invoices={invoices} start={start} end={end} rangeLabel={RANGES.find((r) => r.key === range)!.label} />
      ) : null}

      {hasFeature("aiInsights") ? (
        <AskAiCard tenantId={tenantId ?? ""} digest={digest} rangeLabel={RANGES.find((r) => r.key === range)!.label} />
      ) : (
        <PlanLocked feature="aiInsights" />
      )}
    </div>
  );
}

const SUGGESTED = [
  "What should I focus on this week?",
  "Which customers are the most overdue, and what would you say to them?",
  "How is revenue trending, and why?",
];

function AskAiCard({
  tenantId,
  digest,
  rangeLabel,
}: {
  tenantId: string;
  digest: Record<string, unknown>;
  rangeLabel: string;
}) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function ask(q: string) {
    if (!q.trim() || busy) return;
    setBusy(true);
    setError("");
    setAnswer("");
    try {
      // The digest is the same aggregate shown above — no raw records leave.
      const { agingRows, ...compact } = digest as { agingRows: unknown[] } & Record<string, unknown>;
      const res = await call<{ answer: string }>("askBusinessAi", {
        tenantId,
        question: q.trim(),
        rangeLabel,
        digest: { ...compact, worstOverdue: (agingRows as Array<Record<string, unknown>>).slice(0, 10) },
      });
      setAnswer(res.answer);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardTitle>Ask Clevis AI</CardTitle>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {SUGGESTED.map((s) => (
          <button
            key={s}
            className="rounded-full border border-steel/30 px-2.5 py-1 text-[11px] text-steel hover:border-brandorange hover:text-ink"
            onClick={() => {
              setQuestion(s);
              void ask(s);
            }}
          >
            {s}
          </button>
        ))}
      </div>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void ask(question);
        }}
      >
        <Input
          placeholder="Ask anything about these numbers…"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
        />
        <Button type="submit" busy={busy}>
          Ask
        </Button>
      </form>
      {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
      {answer ? <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-ink">{answer}</p> : null}
    </Card>
  );
}
